/**
 * Semantic intent classification for worksheet-column requests.
 *
 * Answers "which subject is this request asking for, once per company row?"
 * so routing no longer depends on the user picking words from an allowlist.
 * Two stages:
 *
 * 1. An exact hit against the catalog's names and aliases, after stripping
 *    filler verbs and punctuation. This makes one-word requests deterministic
 *    and free, and keeps neighbours that embed closely — CEO and CFO — from
 *    depending on similarity margins.
 * 2. Cosine similarity against one vector per alias, pooled to the best match
 *    per topic. Per-alias vectors matter for terse input: a bare "prior close"
 *    matches the alias of the same length instead of being diluted against a
 *    concatenated paragraph of every phrasing a topic accepts.
 *
 * Every failure path returns `usedEmbeddings: false` so the caller can fall
 * back to the lexical detector rather than silently declining to route.
 */

import { cosineSimilarity, embedTexts, EMBEDDING_MODEL } from "../shared/services/embeddingsService";
import type { IdLookupRequestedField } from "./idLookupRoutingPolicy";
import {
  computeTemplateCatalogFingerprint,
  loadPersistedTemplateEmbeddings,
  persistTemplateEmbeddings,
  type PersistedTemplateEmbeddingEntry,
} from "./templateEmbeddingStore";
import {
  buildWorksheetTopicProbes,
  getWorksheetTopic,
  WORKSHEET_TOPIC_CATALOG,
  type WorksheetTopicOwner,
  type WorksheetTopicProbe,
  type WorksheetTopicRow,
} from "./worksheetTopicCatalog";

export interface WorksheetColumnIntent {
  owner: WorksheetTopicOwner;
  topicKey?: string;
  topicName?: string;
  idLookupField?: IdLookupRequestedField;
  score: number;
  confidence: "high" | "medium" | "low";
  /**
   * False when no vectors were consulted — either an exact alias hit made them
   * unnecessary, or the embedding service was unreachable. Callers use this to
   * decide whether a `none` verdict is a real decline or a missing signal.
   */
  usedEmbeddings: boolean;
  matchedAlias?: string;
}

const LOG_TAG = "Excel-TopicEmbed";
const QUERY_CACHE_LIMIT = 16;
const DEFAULT_TIMEOUT_MS = 1500;

/** Above this the top topic is taken as settled. */
const CONFIDENCE_HIGH_SCORE = 0.62;
/** Below this nothing in the catalog is close enough to act on. */
const CONFIDENCE_LOW_SCORE = 0.42;
/** A near-tie between two topics is reported as low confidence. */
const CONFIDENCE_TIE_GAP = 0.04;

const NO_MATCH: WorksheetColumnIntent = {
  owner: "none",
  score: 0,
  confidence: "low",
  usedEmbeddings: false,
};

// ---------------------------------------------------------------------------
// Normalization and the exact-alias index
// ---------------------------------------------------------------------------

/**
 * Words carrying no topic meaning. Stripped from both the request and the
 * catalog phrases, so trimming them can never move a request onto a different
 * topic — only onto the same topic's shorter form.
 */
const LEADING_FILLER = new Set([
  "a", "add", "all", "an", "and", "are", "can", "could", "currently",
  "display", "do", "does", "each", "every", "fetch", "fill", "find", "for",
  "get", "gimme", "give", "grab", "here", "i", "in", "insert", "is", "latest",
  "list", "look", "lookup", "me", "my", "need", "of", "please", "populate",
  "provide", "pull", "put", "retrieve", "return", "show", "tell", "the", "up",
  "us", "want", "was", "were", "what", "whats", "which", "who", "will",
  "would", "write", "you", "your",
]);

const TRAILING_FILLER = new Set([
  "all", "column", "columns", "companies", "company", "data", "each", "for",
  "here", "in", "info", "information", "issuer", "issuers", "name", "names",
  "of", "per", "please", "row", "rows", "the", "value", "values",
]);

function normalizePhrase(input: string): string {
  const cleaned = input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  if (!cleaned) return "";

  let tokens = cleaned.split(/\s+/);
  const original = tokens;

  let start = 0;
  while (start < tokens.length && LEADING_FILLER.has(tokens[start])) start += 1;
  let end = tokens.length;
  while (end > start && TRAILING_FILLER.has(tokens[end - 1])) end -= 1;

  tokens = tokens.slice(start, end);
  // Stripping everything means the request was pure filler; fall back to the
  // untrimmed form rather than matching the empty string.
  if (tokens.length === 0) tokens = original;

  return tokens.join(" ");
}

/**
 * Embeddings are too noisy to classify very short, unknown tokens. Exact
 * catalog aliases are resolved before this guard, so supported abbreviations
 * such as CEO, P/E, EPS, TSR, and BVPS remain deterministic.
 */
export function carriesClassifiableSignal(userRequest: string): boolean {
  const normalized = normalizePhrase(userRequest);
  if (!normalized) return false;
  const tokens = normalized.split(" ");
  return tokens.length > 1 || tokens[0].length >= 4;
}

/** Crude singular form so "CEOs" reaches the "CEO" alias. */
function singularize(value: string): string {
  const tokens = value.split(" ");
  const last = tokens[tokens.length - 1];
  if (last.length > 3 && last.endsWith("s") && !last.endsWith("ss")) {
    tokens[tokens.length - 1] = last.slice(0, -1);
    return tokens.join(" ");
  }
  return value;
}

interface AliasHit {
  topic: WorksheetTopicRow;
  phrase: string;
}

let aliasIndex: Map<string, AliasHit> | null = null;

function getAliasIndex(): Map<string, AliasHit> {
  if (aliasIndex) return aliasIndex;
  const index = new Map<string, AliasHit>();
  for (const topic of WORKSHEET_TOPIC_CATALOG) {
    for (const phrase of [topic.name, ...topic.aliases]) {
      const normalized = normalizePhrase(phrase);
      if (!normalized) continue;
      // First writer wins so catalog order decides collisions deterministically.
      if (!index.has(normalized)) index.set(normalized, { topic, phrase });
      const singular = singularize(normalized);
      if (singular !== normalized && !index.has(singular)) {
        index.set(singular, { topic, phrase });
      }
    }
  }
  aliasIndex = index;
  return index;
}

function lookupAlias(userRequest: string): AliasHit | undefined {
  const index = getAliasIndex();
  const normalized = normalizePhrase(userRequest);
  if (!normalized) return undefined;
  return index.get(normalized) ?? index.get(singularize(normalized));
}

// ---------------------------------------------------------------------------
// Probe vectors
// ---------------------------------------------------------------------------

const probes: WorksheetTopicProbe[] = buildWorksheetTopicProbes();
const probeVectors = new Map<string, number[]>();
let probeFillPromise: Promise<void> | null = null;

function probeFingerprint(): string {
  return computeTemplateCatalogFingerprint(
    probes.map((probe) => ({ key: probe.id, name: probe.text })),
    `topics:${EMBEDDING_MODEL}`,
    (row) => row.name,
  );
}

async function hydrateProbeVectorsFromDisk(): Promise<void> {
  const persisted = await loadPersistedTemplateEmbeddings(
    probeFingerprint(),
    `topics:${EMBEDDING_MODEL}`,
  );
  if (!persisted) return;
  for (const probe of probes) {
    const entry = persisted.get(probe.id);
    if (entry && entry.text === probe.text) {
      probeVectors.set(probe.id, entry.vector);
    }
  }
}

async function persistProbeVectorsToDisk(): Promise<void> {
  const entries: Record<string, PersistedTemplateEmbeddingEntry> = {};
  for (const probe of probes) {
    const vector = probeVectors.get(probe.id);
    if (vector) entries[probe.id] = { text: probe.text, vector };
  }
  await persistTemplateEmbeddings(
    probeFingerprint(),
    `topics:${EMBEDDING_MODEL}`,
    entries,
  );
}

async function ensureProbeVectors(): Promise<boolean> {
  if (probeVectors.size < probes.length) {
    await hydrateProbeVectorsFromDisk().catch(() => undefined);
  }
  if (probeVectors.size >= probes.length) return true;

  if (probeFillPromise) {
    await probeFillPromise;
    return probeVectors.size >= probes.length;
  }

  probeFillPromise = (async () => {
    try {
      const missing = probes.filter((probe) => !probeVectors.has(probe.id));
      if (missing.length === 0) return;
      // No abort signal: the catalog fill is shared, so one caller cancelling
      // must not empty the cache for everyone else waiting on it.
      const vectors = await embedTexts(
        missing.map((probe) => probe.text),
        { logTag: LOG_TAG },
      );
      if (!vectors) return;
      for (let i = 0; i < missing.length; i++) {
        probeVectors.set(missing[i].id, vectors[i]);
      }
      if (probeVectors.size >= probes.length) {
        void persistProbeVectorsToDisk().catch(() => undefined);
      }
    } finally {
      probeFillPromise = null;
    }
  })();

  await probeFillPromise;
  return probeVectors.size >= probes.length;
}

// ---------------------------------------------------------------------------
// Query vectors
// ---------------------------------------------------------------------------

const queryVectorCache = new Map<string, number[]>();

async function embedQuery(
  text: string,
  signal?: AbortSignal,
): Promise<number[] | null> {
  const cached = queryVectorCache.get(text);
  if (cached) return cached;

  const vectors = await embedTexts([text], { signal, logTag: "Excel-TopicQuery" });
  const vector = vectors?.[0];
  if (!vector) return null;

  if (queryVectorCache.size >= QUERY_CACHE_LIMIT) {
    const oldest = queryVectorCache.keys().next().value;
    if (oldest !== undefined) queryVectorCache.delete(oldest);
  }
  queryVectorCache.set(text, vector);
  return vector;
}

function withTimeout<T>(
  work: Promise<T>,
  timeoutMs: number,
  fallback: T,
): Promise<T> {
  return new Promise<T>((resolve) => {
    const timer = setTimeout(() => resolve(fallback), timeoutMs);
    work
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(fallback);
      });
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

function toIntent(
  topic: WorksheetTopicRow,
  score: number,
  confidence: WorksheetColumnIntent["confidence"],
  usedEmbeddings: boolean,
  matchedAlias?: string,
): WorksheetColumnIntent {
  return {
    owner: topic.owner,
    topicKey: topic.key,
    topicName: topic.name,
    idLookupField: topic.idLookupField,
    score,
    confidence,
    usedEmbeddings,
    matchedAlias,
  };
}

/**
 * Resolves only deterministic catalog aliases. Callers can use this before
 * capability and worksheet-scope guards without triggering embedding I/O.
 */
export function matchWorksheetColumnAlias(
  userRequest: string,
): WorksheetColumnIntent | undefined {
  const request = userRequest?.trim();
  if (!request) return undefined;

  const aliasHit = lookupAlias(request);
  return aliasHit
    ? toIntent(aliasHit.topic, 1, "high", false, aliasHit.phrase)
    : undefined;
}

export async function classifyWorksheetColumnIntent(
  userRequest: string,
  options: { signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<WorksheetColumnIntent> {
  const request = userRequest?.trim();
  if (!request) return NO_MATCH;

  const aliasIntent = matchWorksheetColumnAlias(request);
  if (aliasIntent) return aliasIntent;
  if (!carriesClassifiableSignal(request)) return NO_MATCH;

  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const scored = await withTimeout(
    (async () => {
      const ready = await ensureProbeVectors();
      if (!ready) return null;
      const queryVector = await embedQuery(request, options.signal);
      return queryVector ?? null;
    })(),
    timeoutMs,
    null,
  );

  if (!scored) return NO_MATCH;

  const bestByTopic = new Map<string, number>();
  for (const probe of probes) {
    const vector = probeVectors.get(probe.id);
    if (!vector) continue;
    const score = cosineSimilarity(vector, scored);
    const previous = bestByTopic.get(probe.topicKey);
    if (previous === undefined || score > previous) {
      bestByTopic.set(probe.topicKey, score);
    }
  }

  const ranked = [...bestByTopic.entries()].sort((a, b) => b[1] - a[1]);
  const [topKey, topScore] = ranked[0] ?? [];
  if (!topKey || topScore === undefined || topScore < CONFIDENCE_LOW_SCORE) {
    return { ...NO_MATCH, score: topScore ?? 0, usedEmbeddings: true };
  }

  const topic = getWorksheetTopic(topKey);
  if (!topic) return { ...NO_MATCH, usedEmbeddings: true };

  const runnerUp = ranked[1]?.[1] ?? 0;
  const confidence: WorksheetColumnIntent["confidence"] =
    topScore - runnerUp < CONFIDENCE_TIE_GAP
      ? "low"
      : topScore >= CONFIDENCE_HIGH_SCORE
        ? "high"
        : "medium";

  return toIntent(topic, topScore, confidence, true);
}

/**
 * Fire-and-forget catalog fill. Called once the taskpane is up so the first
 * request does not pay the full-catalog embedding round trip.
 */
export function prewarmWorksheetTopicEmbeddings(
  onLog?: (message: string) => void,
): void {
  void ensureProbeVectors().catch((err) => {
    onLog?.(
      `⚠️ Topic embedding pre-warm failed: ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  });
}

export function resetWorksheetTopicEmbeddingsForTests(): void {
  probeVectors.clear();
  queryVectorCache.clear();
  probeFillPromise = null;
  aliasIndex = null;
}
